import { useMemo } from "react";
import { FilterField } from "../../../types/types";
import DateRangeCalendar from "../DateRangeCalendar/DateRangeCalendar";
import Tag from "../Tag/Tag";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";
import "./FiltersDrawer.scss";
import { useTranslation } from "react-i18next";

type FiltersDrawerProps<T extends Record<string, any>> = {
    filterFields: FilterField<T>[];
    data: T[];
    filterValues: Record<string, any>;
    onChange: (key: string, value: any) => void;
    onClear: () => void;
    onApply?: () => void;
};

function FiltersDrawer<T extends Record<string, any>>({
    filterFields,
    data,
    filterValues,
    onChange,
    onClear,
    onApply,
}: FiltersDrawerProps<T>) {
    const { t } = useTranslation();
const optionsMap = useMemo(() => {
  const map: Record<string, { label: string; value: any }[]> = {};
  // helpers de normalización (mismos principios que en Table.tsx)
  const norm = (x: any) =>
    typeof x === "string" ? x.trim().toLowerCase() : x;
  const selectedValues = (arr: any[]) =>
    arr
      .map((v) => (v && typeof v === "object" && "value" in v ? (v as any).value : v))
      .map(norm);

  filterFields.forEach(({ key, type, labelMap, objectProp }) => {
    if (type === "select" || type === "multiselect" || type === "cards") {
      const otherFilters = Object.entries(filterValues).filter(
        ([k, v]) =>
          k !== String(key) &&
          v !== undefined &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0)
      );

      const temp = data.filter((row) => {
        return otherFilters.every(([k, v]) => {
          const fieldDef = filterFields.find((f) => String(f.key) === k);
          if (!fieldDef) return true;

          const cellRaw = (row as any)[k];
          // Para comparar, toma el "valor visible" (objectProp si aplica) y normaliza
          const cellVal =
            fieldDef.type === "multiselect" && fieldDef.objectProp && cellRaw && typeof cellRaw === "object"
              ? (cellRaw as any)[fieldDef.objectProp]
              : cellRaw;

          if (fieldDef.type === "multiselect" && Array.isArray(v)) {
            const chosen = selectedValues(v);
            return chosen.includes(norm(cellVal));
          }
          if (fieldDef.type === "select") {
            // v ya viene como primitivo seleccionado -> normaliza ambos
            return norm(cellVal) === norm(v);
          }
          if (fieldDef.type === "date" && (v as any)?.start && (v as any)?.end) {
            const d = new Date(String(cellVal));
            return d >= (v as any).start && d <= (v as any).end;
          }
          // texto genérico
          return String(cellVal).toLowerCase().includes(String(v).toLowerCase());
        });
      });

      const unique = Array.from(
        new Set(
          temp.map((r) => {
            const cell = r[key as keyof T];
            const raw =
              objectProp && cell && typeof cell === "object"
                ? (cell as any)[objectProp]
                : cell;
            // evita strings con espacios raros
            return typeof raw === "string" ? raw.trim() : raw;
          })
        )
      ).filter((val) => val !== "" && val !== null && val !== undefined);

      map[String(key)] = unique.map((val) => {
        const label =
          labelMap && typeof val === "number" && (labelMap as any)[val]
            ? (labelMap as any)[val]
            : String(val);
        return { label, value: val };
      });
    }
  });

  return map;
}, [filterFields, data, filterValues]);

    return (
        <div className="filters-drawer">
            <h2 className="modal-title">{t('common.filters')}</h2>

            <div className="filters-drawer__fields">
                {filterFields.map(({ key, label, type }) => {
                    const value = filterValues[key as string];

                    if (type === "select" || type === "multiselect") {
                        return (
                            <div key={String(key)} className="filter-field">
                                <InputDropdown_New
                                    label={label}
                                    options={optionsMap[String(key)] ?? []}
                                    defaultValue={
                                        type === "multiselect"
                                            ? value || []
                                            : value !== undefined &&
                                              value !== null
                                            ? { label: String(value), value }
                                            : undefined
                                    }
                                    placeholder={`${t('common.select')} ${label}`}
                                    searchable={true}
                                    multiple={type === "multiselect"}
                                    onChange={(selected) => {
                                        if (type === "multiselect") {
                                            return onChange(
                                                key as string,
                                                selected
                                            );
                                        } else {
                                            return onChange(
                                                key as string,
                                                (selected as { value: any })
                                                    .value
                                            );
                                        }
                                    }}
                                    required={false}
                                />
                            </div>
                        );
                    }

                    if (type === "date") {
                        return (
                            <div key={String(key)} className="filter-field">
                                <DateRangeCalendar
                                    label={label}
                                    initialStartDate={
                                        value && value.start
                                            ? new Date(value.start)
                                            : null
                                    }
                                    initialEndDate={
                                        value && value.end
                                            ? new Date(value.end)
                                            : null
                                    }
                                    onAccept={({
                                        start,
                                        end,
                                    }: {
                                        start: Date;
                                        end: Date;
                                    }) => {
                                        onChange(key as string, { start, end });
                                    }}
                                    onClear={() => {
                                        onChange(key as string, undefined);
                                    }}
                                />
                            </div>
                        );
                    }

                    if (type === "cards") {
                        return (
                            <div key={String(key)} className="filter-field">
                                <label className="filter-field__label">
                                    {label}
                                </label>
                                <div className="filter-field__cards">
                                    {optionsMap[String(key)].map((option) => (
                                        <Tag
                                            key={option.value}
                                            label={option.label}
                                            isSelected={
                                                Array.isArray(value) &&
                                                value.includes(option.value)
                                            }
                                            onSelect={() => {
                                                const valueOpt = option.value;
                                                const newVal = Array.isArray(
                                                    value
                                                )
                                                    ? value.includes(valueOpt)
                                                        ? value.filter(
                                                              (v) =>
                                                                  v !== valueOpt
                                                          )
                                                        : [...value, valueOpt]
                                                    : [valueOpt];
                                                onChange(key as string, newVal);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        </div>
    );
}

export default FiltersDrawer;
